import type { DefaultOptionType } from 'rc-select/lib/Select';
import React from 'react';
import { Link } from 'react-router-dom';

import { prefetchOptions, toLabel } from '~/components/Columns/helpers';

import { type CollectionColumnDefinition, type ColumnDefinition, ModelColumns } from '~/types/components';
import { defaultcallDelayLabel, groupCallStrategyLabel } from '~/types/models';
import type { Contact, Group, GroupCallStrategy, User, UserGroup } from '~/types/models';

import { labelsToOptions, stripHtml, tc } from '~/helpers';

const groupContactsColumn: CollectionColumnDefinition<Group, Contact> = {
  title: 'Contacts',
  key: 'contacts',
  only: ['single'],
  sortable: false,
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return '';
    }

    if (value.length === 0) {
      return <em>Aucun contact</em>;
    }

    if (value.length > 4) {
      return (
        <>
          <strong>{value.length} contacts</strong>
          <ul className='CollectionColumn' style={{ margin: 0 }}>
            {value.map((c) => (
              <li key={c.id}>
                {c.firstName} {c.lastName}
              </li>
            ))}
          </ul>
        </>
      );
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((c) => (
          <li key={c.id}>
            {c.firstName} {c.lastName}
          </li>
        ))}
      </ul>
    );
  },
};

const groupUsersColumn: CollectionColumnDefinition<Group, User> = {
  title: 'Participant·es',
  key: 'users',
  mapOptions(items: User[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  only: ['single'],
  prefetchOptions: prefetchOptions<User>('users'),
  render: (value) => {
    if (!Array.isArray(value)) {
      return '';
    }

    if (value.length === 0) {
      return <em>Aucun·e participant·e</em>;
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((u) => (
          <li key={u.id}>{u.name}</li>
        ))}
      </ul>
    );
  },
  sortable: false,
  toLabel: toLabel('users', 'Participant·e'),
  type: 'collection',
};

const groupUserGroupsColumn: CollectionColumnDefinition<Group, UserGroup> = {
  title: 'Permissions',
  filterable: false,
  key: 'userGroups',
  mapOptions(items: any[]): DefaultOptionType[] {
    return items.map((i) => ({
      value: i.id,
      key: i.id,
      label: i.name,
    }));
  },
  prefetchOptions: prefetchOptions('users'),
  sortable: false,
  type: 'collection',
  render: (value) => {
    if (!Array.isArray(value)) {
      return <em>Chargement...</em>;
    }

    if (value.length === 0) {
      return <em>Aucune permission</em>;
    }

    return (
      <ul className='CollectionColumn' style={{ margin: 0 }}>
        {value.map((u) => (
          <li key={u.id}>
            {u.user?.name}
            <ul>{u.roles?.map((r) => <li key={r.id}>{r.name}</li>)}</ul>
          </li>
        ))}
      </ul>
    );
  },
  with: 'userGroups,userGroups.roles,userGroups.user',
};

export const Columns: Array<ColumnDefinition<Group>> = [
  {
    title: "Nb d'appels",
    filterable: false,
    importable: false,
    key: 'callsCount',
    sortable: false,
    type: 'count',
    render: (count) => tc('Aucun appel|{count} appel|{count} appels', count),
  },
  {
    title: "Stratégie d'affectation des appels",
    key: 'callStrategy',
    render(value: GroupCallStrategy) {
      return React.createElement('span', {}, groupCallStrategyLabel[value] ?? 's. o.');
    },
    sortable: true,
    type: 'enum',
    options: labelsToOptions(groupCallStrategyLabel),
  },
  {
    title: 'Nb de contacts',
    filterable: false,
    importable: false,
    key: 'contactsCount',
    sortable: false,
    type: 'count',
    render: (count, record) => {
      return (
        <Link to={`/contacts?page=1&groups[0]=${record?.id ?? ''}`}>
          {tc('{count} contact|{count} contacts', count)}
        </Link>
      );
    },
  },
  {
    title: 'Intervalle par défaut entre les appels',
    key: 'defaultCallDelay',
    sortable: false,
    type: 'enum',
    options: labelsToOptions(defaultcallDelayLabel),
  },
  {
    title: 'Description',
    key: 'description',
    sortable: false,
    type: 'string',
  },
  {
    title: 'Nom',
    key: 'name',
    sortable: true,
    type: 'string',
  },
  {
    title: 'Script',
    key: 'script',
    sortable: false,
    type: 'string',
    render: (value) => stripHtml(value),
  },
  groupContactsColumn,
  groupUsersColumn,
  groupUserGroupsColumn,
  ...ModelColumns,
];
